import {
  forwardRef,
  ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import IconTrash from "@/assets/iconTrash.svg?react";
import {Button} from "@/components/Button/Button";
import {
  quicksandMedium,
  quicksandRegular,
  quicksandSemiBold,
  shadowButton,
} from "@/GlobalStyle";
import {OptionNameValue} from "@/ts/formulaire";

import {SearchInputSelectCustom} from "./SearchInputSelectCutom";

interface SelectCustomProps {
  canSearch?: boolean;
  defaultValue?: string;
  id?: string;
  options: OptionNameValue[];
  name: string;
  disabled?: boolean;
  placeholder?: string;
  handleSelect: (value: any, libelle: any, complementData: any) => void;
  setValue?: (name: string, value: any) => void;
  watch?: (name: string) => any;
  label?: string;
  listIsAbsolute?: boolean; // Déclaration propre
  complementLabel?: string;
  onTrash?: () => void;
  title?: string;
  testId?: string;
}

const SelectCustomComposant = styled.div<{$disabled: boolean}>`
  position: relative;
  ${({$disabled, theme}) =>
    $disabled
      ? `cursor: not-allowed !important;

    & label {
      color: ${theme.colors.shadow};
      cursor: not-allowed;
    }
    & .selectCustom-current {
      background-color: ${theme.colors.grey};
      border: 2px solid ${theme.colors.grey};
      cursor: not-allowed !important;

      & p {
        color: ${theme.colors.shadow};
      }
    }
    & #triangle-code {
      border-right: 6px solid transparent;
      border-bottom: 12px solid ${theme.colors.shadow};
      border-left: 7px solid transparent;
    }`
      : ""}
`;

const LabelSelect = styled.label`
  display: block;
  font-size: 1rem;

  color: ${({theme}) => theme.colors.darkPrimary};
  ${quicksandSemiBold};
  & span {
    @include quicksand-regular();
    font-size: 0.75rem;
  }
`;

const LabelSelectBlock = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  min-height: 35px;

  & button {
    margin: 5px 0 7px 5px;
    padding: 6px 8px;
  }
`;

const ListContent = styled.ul<{$isAbsolute: boolean; $isOpen: boolean}>`
  background-color: white;
  margin-top: 5px;
  padding: 0;
  border-radius: 7px;
  border: 2px solid ${({theme}) => theme.colors.mediumSecondary};
  width: -webkit-fill-available;
  width: -moz-available;
  z-index: 1;
  ${shadowButton};
  display: ${({$isOpen}) => ($isOpen ? "block" : "none")};
  position: ${({$isAbsolute}) => ($isAbsolute ? "absolute" : "relative")};
  max-height: 300px;
  overflow-y: auto;
  &:hover .selected {
    background-color: white;
    color: ${({theme}) => theme.colors.grey};
  }
  & li {
    list-style: none;
    color: ${({theme}) => theme.colors.grey};
    ${quicksandSemiBold}
    padding: 5px;
    font-size: 0.875rem;
    border-bottom: 1px solid ${({theme}) => theme.colors.mediumSecondary};
    &.selected {
      background-color: ${({theme}) => theme.colors.mediumSecondary};
      color: white;
    }
    & span {
      color: ${({theme}) => theme.colors.darkPrimary};
      ${quicksandRegular}
      font-size: 0.75rem;
    }
    &:hover {
      background-color: ${({theme}) => theme.colors.mediumSecondary} !important;
      color: white !important;
      cursor: pointer;
      & span {
        color: white;
      }
    }
  }
`;

const SelectCustumCurrentValue = styled.div<{
  $hasOption: boolean;
  $disabled: boolean;
}>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 15px;
  background-color: transparent;
  border: none;
  ${quicksandSemiBold}
  padding: 10px 15px 10px 10px;
  min-height: 21px;
  width: inherit;
  border-radius: 7px;
  border: 2px solid
    ${({theme, $disabled}) =>
      $disabled ? theme.colors.shadow : theme.colors.mediumSecondary};
  background-color: white;
  text-align: left;
  cursor: ${({$hasOption, $disabled}) =>
    $disabled ? "not-allowed" : $hasOption ? "pointer" : "default"};
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  & p {
    font-size: 0.875rem;
    color: ${({$disabled, theme}) =>
      $disabled ? theme.colors.shadow : "black"};
  }
  @media (max-width: 500px) {
    padding: 8px 10px 8px 5px;
    gap: 10px;
  }
`;

const PlaceholderSpan = styled.span`
  font-size: 0.875rem;
  color: ${({theme}) => theme.colors.PlaceholderColor};
  opacity: 0.4;
  ${quicksandMedium}
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  overflow: hidden;
`;

const Triangle = styled.div<{$isOpen: boolean; $disabled: boolean}>`
  display: ${({$disabled}) => ($disabled ? "none" : "inline-block")};
  height: 0;
  width: 0;
  border-right: 6px solid transparent;
  border-bottom: 12px solid ${({theme}) => theme.colors.mediumSecondary};
  border-left: 7px solid transparent;
  transform: ${({$isOpen}) => ($isOpen ? "" : "rotate(180deg)")};
`;

const SelectCustom = forwardRef<HTMLDivElement, SelectCustomProps>(
  function SelectCustom(
    {
      canSearch = false,
      defaultValue,
      options,
      id,
      placeholder = "",
      disabled = false,
      handleSelect,
      setValue,
      watch,
      label,
      name,
      listIsAbsolute = true,
      complementLabel = "",
      onTrash,
      ...rest
    },
    ref
  ): ReactElement {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const currentValue = watch
      ? watch(`${name}.libelle`) || defaultValue
      : defaultValue;
    const inputSearchRef = useRef<HTMLInputElement>(null);

    const selectRef = useRef<HTMLDivElement>(null);
    const comopasantRef = useRef<HTMLDivElement>(null);

    const handleSelectOption = (option: OptionNameValue): void => {
      setIsOpen(false);
      setSelectedIndex(-1);
      if (setValue) {
        setValue(`${name}.libelle`, option.libelle);
        setValue(`${name}.value`, option.value);
        setValue(`${name}.complementData`, option.complementData);
      }
      handleSelect(option.value, option?.libelle, option?.complementData);
    };

    const handleSearch = (): OptionNameValue[] => {
      return options.filter(option => {
        return option.libelle
          .toString()
          .toUpperCase()
          .includes(searchTerm.toUpperCase());
      });
    };
    // Permet à RHF d'utiliser le ref
    useImperativeHandle(ref, () => selectRef.current as HTMLDivElement);
    const optionsList = handleSearch()?.map((option, index) => {
      const isSelected = index === selectedIndex;
      return (
        <li
          key={index}
          onClick={() => handleSelectOption(option)}
          className={isSelected ? "selected" : ""}>
          {option.libelle}
          <span>{` ${option.complementLibelle || ""}`}</span>
        </li>
      );
    });

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (
          comopasantRef.current &&
          !comopasantRef.current.contains(event.target as Node) &&
          isOpen
        ) {
          setIsOpen(false);
          setSelectedIndex(-1);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return (): void => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, selectRef]);
    // permet de focus l'input de recherche quand on ouvre la liste déroulante
    useEffect(() => {
      if (isOpen) {
        inputSearchRef?.current?.focus();
      } else if (inputSearchRef?.current) {
        setSearchTerm("");
        inputSearchRef.current.value = "";
      }
    }, [isOpen]);
    const hasOption: boolean = optionsList.length > 0;
    const handleClick = (): void => {
      if (!disabled && hasOption) {
        setIsOpen(prev => !prev);
        // On cherche l'index de la valeur courante dans la liste des options
        // Si on en trouve pas, on met l'index à 0
        const findIndex = options.findIndex(
          option => option.value === currentValue
        );
        if (findIndex !== -1) {
          setSelectedIndex(findIndex);
        } else {
          setSelectedIndex(0);
        }
      }
      inputSearchRef?.current?.focus();
    };
    // Nouveau : Gestion de la navigation au clavier
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isOpen || !hasOption) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setSelectedIndex(prev =>
            prev < optionsList.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : optionsList.length - 1
          );
          break;
        case "Enter":
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < optionsList.length) {
            const selectedOption = handleSearch()[selectedIndex];
            handleSelectOption(selectedOption);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
      }
    };

    useEffect(() => {
      if (isOpen) {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }
    }, [isOpen, selectedIndex, searchTerm, options]);
    return (
      <SelectCustomComposant
        $disabled={disabled}
        ref={comopasantRef}
        id={id}
        {...rest}
        data-testid={rest.testId}>
        {label ? (
          <LabelSelectBlock>
            <LabelSelect htmlFor="">
              {label}
              <span>{` ${complementLabel}`}</span>
            </LabelSelect>
            {onTrash && currentValue ? (
              <Button
                onClick={() => {
                  if (!disabled) onTrash();
                }}
                variant={"secondary"}
                icon={IconTrash}
              />
            ) : null}
          </LabelSelectBlock>
        ) : null}
        <SelectCustumCurrentValue
          $hasOption={hasOption}
          $disabled={disabled}
          onClick={handleClick}>
          <p>
            {currentValue || <PlaceholderSpan>{placeholder}</PlaceholderSpan>}
          </p>
          {hasOption ? (
            <Triangle $isOpen={isOpen} $disabled={disabled} />
          ) : null}
        </SelectCustumCurrentValue>
        <div ref={selectRef}>
          <ListContent $isOpen={isOpen} $isAbsolute={listIsAbsolute}>
            {canSearch ? (
              <SearchInputSelectCustom
                testId={`search-input-${rest.testId}`}
                ref={inputSearchRef}
                placeholder="Recherche adhérent"
                onChange={() => {
                  if (inputSearchRef?.current?.value !== undefined) {
                    setSearchTerm(inputSearchRef?.current?.value);
                  }
                }}
              />
            ) : null}
            {optionsList}
          </ListContent>
        </div>
      </SelectCustomComposant>
    );
  }
);

export default SelectCustom;
